import React from 'react';
import { Box, Grid, Tooltip, TypographyProps } from '@mui/material';
import { Grid2Props } from '@mui/material/Unstable_Grid2';

import { TextField, Typography, InfoOutlinedIcon, Select } from '@occc-app/design-system';
import { parseNumber } from '@occc-app/lib/finalFormUtils';
import { SteeringWalletBasis, Unit_Of_Measure } from '@occc/graphql';

import { ProductGroupSelectNotMultiple } from './ProductGroupSelectNotMultiple';
import { IncentivizeResponses } from './IncentivizeResponses';

import { titleByUnitOfMeasure } from '../constants';

const Name: React.FC = () => (
  <Grid item xs={12} md={6}>
    <Typography variant='h6' mb={2}>
      Wallet Name
    </Typography>
    <TextField name='name' label='Name' margin='none' />
  </Grid>
);

const SurveyQuestion: React.FC = () => (
  <Grid item xs={12} md={6}>
    <Typography variant='h6' mb={2}>
      Wallet Survey Question
    </Typography>

    <TextField name='question' label='Question' margin='none' />
  </Grid>
);

const countWalletBy = [
  { label: titleByUnitOfMeasure[Unit_Of_Measure.Amount], value: Unit_Of_Measure.Amount },
  { label: titleByUnitOfMeasure[Unit_Of_Measure.Gallon], value: Unit_Of_Measure.Gallon },
  { label: titleByUnitOfMeasure[Unit_Of_Measure.Unit], value: Unit_Of_Measure.Unit },
];

const periodOptions = [
  { label: 'Daily', value: SteeringWalletBasis.Daily },
  { label: 'Weekly', value: SteeringWalletBasis.Weekly },
  { label: 'Monthly', value: SteeringWalletBasis.Monthly },
];

const WalletConfiguration: React.FC<{ disabled?: boolean }> = ({ disabled = false }) => (
  <>
    <Grid item xs={12} md={6}>
      <Typography variant='h6' mb={2} color={disabled ? 'textSecondary' : undefined}>
        Include All Products In
      </Typography>
      <ProductGroupSelectNotMultiple
        name='productGroup'
        label='Product group'
        disabled={disabled}
      />
    </Grid>

    <Grid item sm={12} md={4}>
      <Typography variant='h6' mb={2} color={disabled ? 'textSecondary' : undefined}>
        Count Wallet By
      </Typography>
      <Select
        name='countWalletBy'
        data={countWalletBy}
        formControlProps={{ size: 'medium', margin: 'none' }}
        disabled={disabled}
      />
    </Grid>

    <Grid item xs={12} sm={6} md={2}>
      <Typography variant='h6' mb={2} sx={{ visibility: 'hidden' }}>
        Period
      </Typography>

      <Select
        name='period'
        label='Period'
        data={periodOptions}
        formControlProps={{ size: 'medium', margin: 'none' }}
        disabled={disabled}
      />
    </Grid>
  </>
);

type RangeProps = {
  gridItemProps?: Grid2Props;
  titleVariant?: TypographyProps['variant'];
};

const Range = ({ gridItemProps, titleVariant }: RangeProps) => (
  <>
    <Grid item xs={6} md={2} {...gridItemProps}>
      <Tooltip title='Minimum and maximum values in a Wallet help ensure accurate data analysis by eliminating outliers and setting valid response range.'>
        <Box display='flex' flexDirection='row' mb={2}>
          <Typography variant={titleVariant || 'h6'}>Range</Typography>

          <Box display='flex' justifyContent='center' alignItems='center' ml={0.5}>
            <InfoOutlinedIcon sx={{ fontSize: '2rem', color: 'info.main' }} />
          </Box>
        </Box>
      </Tooltip>

      <TextField
        name='rangeMin'
        label='Minimum'
        fieldProps={{
          parse: parseNumber,
        }}
      />
    </Grid>

    <Grid
      item
      xs={6}
      md={2}
      display='flex'
      alignItems='flex-end'
      flexDirection='column'
      {...gridItemProps}
    >
      <Typography variant='h6' mb={2} sx={{ visibility: 'hidden' }}>
        Maximum
      </Typography>

      <TextField
        name='rangeMax'
        label='Maximum'
        fieldProps={{
          parse: parseNumber,
        }}
      />
    </Grid>
  </>
);

export const walletFormFields = {
  Name,
  SurveyQuestion,
  WalletConfiguration,
  Range,
  IncentivizeResponses,
};
